import { Autocomplete, Chip, TextField, TextFieldProps } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form'

type CreatableOptionProps = {
    label: string
    /**
     * Quando definido, ao criar uma nova opção, o valor será usado
     * como o valor do campo. Se não for definido, o novo valor será criado
     * mas não será usado como valor do campo.
     */
    value?: string
    onClick: () => void
}

type FormFieldAutocompleteProps = Omit<UseControllerProps, 'control'> &
    Omit<TextFieldProps, 'name' | 'value' | 'ref'> & {
        options: ReadonlyArray<any>
        getOptionLabel?: (option: any) => string
        creatableOption?: CreatableOptionProps
        multiple?: boolean
        limitTags?: number
        unHelperText?: boolean
        onInputChange?: (inputValue: string) => void
        noOptionsText?: string
        disableClearable?: boolean
        isOptionEqualToValue?: (option: any, value: any) => boolean
    }

export const FormFieldAutocomplete: FC<FormFieldAutocompleteProps> = ({
    name,
    rules,
    options,
    getOptionLabel,
    variant = 'filled',
    creatableOption,
    multiple = false,
    limitTags,
    unHelperText,
    shouldUnregister,
    onInputChange,
    noOptionsText = 'Nenhuma opção encontrada',
    disableClearable = false,
    isOptionEqualToValue,
    ...rest
}) => {
    const { control, watch, setValue } = useFormContext()
    const [inputValue, setInputValue] = useState('')

    const disabled = rest.disabled || (control as any)._options?.disabled

    let defaultValue: any

    if (multiple) {
        defaultValue = rest.defaultValue || []
    } else {
        defaultValue = rest.defaultValue || null
    }

    const component = watch(name, true)

    useEffect(() => {
        if (creatableOption && component) {
            if (multiple) {
                const includeCreatable = component.some(
                    (item: any) => item?.label === creatableOption.label,
                )

                if (includeCreatable) {
                    const newComponent = component.filter(
                        (item: any) => item?.label !== creatableOption.label,
                    )
                    creatableOption.onClick()
                    setInputValue('')
                    setValue(name, newComponent)
                }
            } else if (component?.label === creatableOption.label) {
                creatableOption.onClick()
                setInputValue('')
                setValue(name, creatableOption.value)
            }
        }
    }, [component, creatableOption, name, setValue])

    // Notifica o componente pai sobre mudanças no input
    useEffect(() => {
        if (onInputChange) {
            onInputChange(inputValue)
        }
    }, [inputValue, onInputChange])

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            shouldUnregister={shouldUnregister}
            render={({
                field: { onChange, value, ...field },
                fieldState: { error },
            }) => (
                <Autocomplete
                    defaultValue={defaultValue}
                    value={value}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue) => {
                        setInputValue(newInputValue)
                    }}
                    noOptionsText={noOptionsText}
                    disableClearable={disableClearable}
                    isOptionEqualToValue={(option, value) =>
                        (() => {
                            if (isOptionEqualToValue)
                                return isOptionEqualToValue(option, value)
                            const realOption =
                                typeof option !== 'object' || !option
                                    ? option
                                    : option.value || option.label || option
                            const realValue =
                                typeof value !== 'object' || !value
                                    ? value
                                    : value.value || value.label || value
                            return realOption === realValue
                        })()
                    }
                    multiple={multiple}
                    limitTags={limitTags}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <React.Fragment key={index}>
                                {!option.label && (
                                    <Chip
                                        variant="filled"
                                        label={
                                            getOptionLabel
                                                ? getOptionLabel(option)
                                                : option.label
                                        }
                                        size="small"
                                        {...getTagProps({ index })}
                                    />
                                )}
                            </React.Fragment>
                        ))
                    }
                    onChange={(_, value) => onChange(value)}
                    options={
                        creatableOption
                            ? [creatableOption, ...options]
                            : options
                    }
                    getOptionLabel={option => {
                        if (getOptionLabel) return getOptionLabel(option)

                        if (typeof option.label === 'string')
                            return option.label

                        return option
                    }}
                    readOnly={disabled}
                    disabled={disabled}
                    renderInput={autoCompleteProps => (
                        <TextField
                            {...rest}
                            {...autoCompleteProps}
                            {...field}
                            disabled={disabled}
                            variant={variant}
                            fullWidth
                            error={!!error}
                            helperText={
                                error && !unHelperText
                                    ? error.message
                                    : rest?.helperText
                                      ? rest.helperText
                                      : unHelperText
                                        ? null
                                        : ' '
                            }
                            InputProps={{
                                ...autoCompleteProps.InputProps,
                                ...rest.InputProps,
                                endAdornment: (
                                    <>
                                        {rest.InputProps?.endAdornment}
                                        {
                                            autoCompleteProps.InputProps
                                                ?.endAdornment
                                        }
                                    </>
                                ),
                            }}
                            InputLabelProps={{
                                ...autoCompleteProps.InputLabelProps,
                                ...rest.InputLabelProps,
                            }}
                        />
                    )}
                />
            )}
        />
    )
}
